import { useCallback, useEffect, useMemo, useState } from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount, useWaitForTransactionReceipt } from "wagmi";
import { formatEther, parseEther } from "viem";
import { Link } from "react-router-dom";
import { RiDatabase2Line } from "@remixicon/react";
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/sidebar";
import { Card, CardContent } from "@/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { Button } from "@/components/button";
import { Input } from "@/components/input";
import { Badge } from "@/components/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/sheet";
import { SILO_NODE_ABI, SILO_NODE_CONTRACT } from "@/abis/silo-node";
import { publicClient } from "@/configs/client";
import { useSiloNodeActions } from "@/hooks/use-silo-node-actions";
import { useStats } from "@/providers/global-data.provider";

type NodeData = {
  registered: boolean;
  provider: `0x${string}`;
  delegatorShareBps: number;
  providerStake: bigint;
  totalDelegated: bigint;
  pendingProviderReward: bigint;
  exitRequested: boolean;
  exitUnlocked: boolean;
};

type DelegationData = {
  amount: bigint;
  rewardDebt: bigint;
  pendingReward: bigint;
};

type GlobalNodeStats = {
  totalProviderPrincipal: bigint;
  totalDelegatorPrincipal: bigint;
  totalPendingProviderRewards: bigint;
  totalPendingDelegatorRewards: bigint;
  totalLiabilities: bigint;
  freshUndistributedRewards: bigint;
  minProviderStake: bigint;
  minDelegation: bigint;
};

type ApiNodeItem = {
  nodeId: string;
  registered: number | boolean;
  provider: string;
  delegatorShareBps: number;
  providerStake: string;
  totalDelegated: string;
  pendingProviderReward: string;
  stats?: {
    email?: string | null;
    lastContactSuccessAt?: string | null;
    totalStorage?: string | null;
  } | null;
  estimatedReward?: {
    wei?: string;
    weightScore?: number;
  } | null;
};

type DelegatedNodeDetails = {
  nodeId: string;
  provider: `0x${string}`;
  delegatorShareBps: number;
  delegatedAmount: bigint;
  pendingReward: bigint;
  totalNodeDelegation: bigint;
  averageDelegatorRewardPerEpoch: bigint;
  estimatedNextEpochEarningLRS: bigint;
};

type DelegatorNodeStats = {
  totalDelegated: bigint;
  totalUndelegated: bigint;
  totalClaimedRewards: bigint;
  lastActionAt: bigint;
  lastClaimAt: bigint;
};

type NodeRewardStats = {
  totalSubmittedRewards: bigint;
  totalProviderRewards: bigint;
  totalDelegatorRewards: bigint;
  rewardSubmissionCount: bigint;
  lastSubmittedAt: bigint;
  lastSubmittedReward: bigint;
  totalProviderClaimed: bigint;
};

const EMPTY_GLOBAL_STATS: GlobalNodeStats = {
  totalProviderPrincipal: 0n,
  totalDelegatorPrincipal: 0n,
  totalPendingProviderRewards: 0n,
  totalPendingDelegatorRewards: 0n,
  totalLiabilities: 0n,
  freshUndistributedRewards: 0n,
  minProviderStake: 0n,
  minDelegation: 0n,
};

const NODE_LEADERBOARD_API_URL =
  import.meta.env.VITE_SILO_NODES_API_URL || "http://127.0.0.1:3006/api/nodes";

function formatUsdFromLrs(lrsAmount: number, lrsUsdPrice: number): string | null {
  if (!Number.isFinite(lrsAmount) || !Number.isFinite(lrsUsdPrice) || lrsUsdPrice <= 0) return null;
  const usd = lrsAmount * lrsUsdPrice;
  if (!Number.isFinite(usd)) return null;
  return usd.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Data-dense reward line: LRS primary, USD secondary (uses live LRS/USD from stats). */
function LrsRewardWithUsd({
  wei,
  lrsUsdPrice,
  primaryClassName = "font-semibold",
}: {
  wei: bigint;
  lrsUsdPrice: number;
  primaryClassName?: string;
}) {
  const lrsAmt = Number(formatEther(wei));
  const usdLabel = formatUsdFromLrs(lrsAmt, lrsUsdPrice);
  return (
    <div className="space-y-0.5">
      <div className={`tabular-nums ${primaryClassName}`}>
        {Number.isFinite(lrsAmt) ? lrsAmt.toFixed(4) : "0.0000"} LRS
      </div>
      {usdLabel && (
        <div className="text-xs text-muted-foreground tabular-nums">≈ {usdLabel}</div>
      )}
    </div>
  );
}

export default function SiloNodesPage() {
  const { address } = useAccount();
  const actions = useSiloNodeActions();
  const { price: lrsPriceUsd } = useStats();

  const [selectedNodeId, setSelectedNodeId] = useState("");
  const [globalStats, setGlobalStats] = useState<GlobalNodeStats>(EMPTY_GLOBAL_STATS);
  const [leaderboardNodes, setLeaderboardNodes] = useState<ApiNodeItem[]>([]);
  const [leaderboardError, setLeaderboardError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<NodeData | null>(null);
  const [delegationData, setDelegationData] = useState<DelegationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>(undefined);

  const [delegatedNodes, setDelegatedNodes] = useState<DelegatedNodeDetails[]>([]);
  const [delegatedNodesTotal, setDelegatedNodesTotal] = useState(0);
  const [delegatorStatsByNode, setDelegatorStatsByNode] = useState<Record<string, DelegatorNodeStats>>({});

  const [delegateSheetOpen, setDelegateSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<"delegate" | "stake" | "unstake" | "undelegate">("delegate");
  const [delegateNodeId, setDelegateNodeId] = useState("");
  const [delegateAmount, setDelegateAmount] = useState("");
  const [sheetNodeData, setSheetNodeData] = useState<NodeData | null>(null);
  const [delegateGasEstimate, setDelegateGasEstimate] = useState<bigint | null>(null);
  const [delegateGasPrice, setDelegateGasPrice] = useState<bigint | null>(null);
  const [delegateEstimateError, setDelegateEstimateError] = useState<string | null>(null);
  const [amountValidationError, setAmountValidationError] = useState<string | null>(null);
  const [estimatedWeeklyRewards, setEstimatedWeeklyRewards] = useState<bigint>(0n);
  const [estimatedCurrentRewards, setEstimatedCurrentRewards] = useState<bigint>(0n);

  const contractReady = Boolean(SILO_NODE_CONTRACT);
  const { isSuccess: txSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
    query: { enabled: !!txHash },
  });

  const formatAmount = (value: bigint) => Number(formatEther(value)).toFixed(4);
  const formatAddress = (value: string) => `${value.slice(0, 6)}...${value.slice(-4)}`;
  const parseBigInt = (value: string | number | null | undefined): bigint => {
    try {
      return BigInt(value ?? 0);
    } catch {
      return 0n;
    }
  };
  const formatStorageGb = (value: string | null | undefined): string => {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes)) return "0.00";
    return (bytes / (1024 * 1024 * 1024)).toFixed(2);
  };
  const toRelativeTime = (value: string | null | undefined): string => {
    if (!value) return "No heartbeat";
    const date = new Date(value);
    const now = Date.now();
    const diffMs = now - date.getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) return "Just now";
    const minutes = Math.floor(diffMs / (1000 * 60));
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };
  const formatDateTime = (value: bigint) => {
    if (!value || value === 0n) return "-";
    return new Date(Number(value) * 1000).toLocaleString();
  };
  const fetchGlobalStats = useCallback(async () => {
    if (!contractReady) {
      setGlobalStats(EMPTY_GLOBAL_STATS);
      return;
    }

    const [
      totalProviderPrincipal,
      totalDelegatorPrincipal,
      totalPendingProviderRewards,
      totalPendingDelegatorRewards,
      totalLiabilities,
      freshUndistributedRewards,
      minProviderStake,
      minDelegation,
    ] = await Promise.all([
      publicClient.readContract({ address: SILO_NODE_CONTRACT, abi: SILO_NODE_ABI, functionName: "totalProviderPrincipal" }),
      publicClient.readContract({ address: SILO_NODE_CONTRACT, abi: SILO_NODE_ABI, functionName: "totalDelegatorPrincipal" }),
      publicClient.readContract({
        address: SILO_NODE_CONTRACT,
        abi: SILO_NODE_ABI,
        functionName: "totalPendingProviderRewards",
      }),
      publicClient.readContract({
        address: SILO_NODE_CONTRACT,
        abi: SILO_NODE_ABI,
        functionName: "totalPendingDelegatorRewards",
      }),
      publicClient.readContract({ address: SILO_NODE_CONTRACT, abi: SILO_NODE_ABI, functionName: "totalLiabilities" }),
      publicClient.readContract({
        address: SILO_NODE_CONTRACT,
        abi: SILO_NODE_ABI,
        functionName: "freshUndistributedRewards",
      }),
      publicClient.readContract({ address: SILO_NODE_CONTRACT, abi: SILO_NODE_ABI, functionName: "minProviderStake" }),
      publicClient.readContract({ address: SILO_NODE_CONTRACT, abi: SILO_NODE_ABI, functionName: "minDelegation" }),
    ]);

    setGlobalStats({
      totalProviderPrincipal: totalProviderPrincipal as bigint,
      totalDelegatorPrincipal: totalDelegatorPrincipal as bigint,
      totalPendingProviderRewards: totalPendingProviderRewards as bigint,
      totalPendingDelegatorRewards: totalPendingDelegatorRewards as bigint,
      totalLiabilities: totalLiabilities as bigint,
      freshUndistributedRewards: freshUndistributedRewards as bigint,
      minProviderStake: minProviderStake as bigint,
      minDelegation: minDelegation as bigint,
    });
  }, [contractReady]);

  const parseNodeData = (raw: any): NodeData => ({
    registered: Boolean(raw.registered),
    provider: raw.provider as `0x${string}`,
    delegatorShareBps: Number(raw.delegatorShareBps),
    providerStake: raw.providerStake as bigint,
    totalDelegated: raw.totalDelegated as bigint,
    pendingProviderReward: raw.pendingProviderReward as bigint,
    exitRequested: Boolean(raw.exitRequested),
    exitUnlocked: Boolean(raw.exitUnlocked),
  });

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLeaderboardError(null);
      const response = await fetch(NODE_LEADERBOARD_API_URL, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error(`Leaderboard load failed: ${response.status}`);
      }
      const result = await response.json();
      const rows = (result?.data || []) as ApiNodeItem[];
      setLeaderboardNodes(rows);
      setSelectedNodeId((prev) => {
        if (prev && rows.some((item) => item.nodeId === prev)) return prev;
        return rows[0]?.nodeId || "";
      });
    } catch (error) {
      setLeaderboardNodes([]);
      setLeaderboardError(error instanceof Error ? error.message : "Failed to fetch leaderboard");
    }
  }, []);

  const fetchDelegatorInfo = useCallback(async () => {
    if (!contractReady || !address) {
      setDelegatedNodes([]);
      setDelegatedNodesTotal(0);
      setDelegatorStatsByNode({});
      return;
    }

    try {
      const result = (await publicClient.readContract({
        address: SILO_NODE_CONTRACT,
        abi: SILO_NODE_ABI,
        functionName: "getDelegatedNodeDetails",
        args: [address, 0n, 100n],
      })) as [any[], bigint];

      const detailsRaw = result[0] || [];
      const total = Number(result[1] || 0n);
      const details: DelegatedNodeDetails[] = detailsRaw.map((item: any) => ({
        nodeId: item.nodeId,
        provider: item.provider,
        delegatorShareBps: Number(item.delegatorShareBps),
        delegatedAmount: item.delegatedAmount as bigint,
        pendingReward: item.pendingReward as bigint,
        totalNodeDelegation: item.totalNodeDelegation as bigint,
        averageDelegatorRewardPerEpoch: item.averageDelegatorRewardPerEpoch as bigint,
        estimatedNextEpochEarningLRS: item.estimatedNextEpochEarningLRS as bigint,
      }));

      setDelegatedNodes(details);
      setDelegatedNodesTotal(total);

      const statsResults = await Promise.all(
        details.map(async (item) => {
          const stats = (await publicClient.readContract({
            address: SILO_NODE_CONTRACT,
            abi: SILO_NODE_ABI,
            functionName: "getDelegatorNodeStats",
            args: [item.nodeId, address],
          })) as any;
          return {
            nodeId: item.nodeId,
            stats: {
              totalDelegated: stats.totalDelegated as bigint,
              totalUndelegated: stats.totalUndelegated as bigint,
              totalClaimedRewards: stats.totalClaimedRewards as bigint,
              lastActionAt: stats.lastActionAt as bigint,
              lastClaimAt: stats.lastClaimAt as bigint,
            } satisfies DelegatorNodeStats,
          };
        }),
      );

      setDelegatorStatsByNode(
        statsResults.reduce(
          (acc, current) => {
            acc[current.nodeId] = current.stats;
            return acc;
          },
          {} as Record<string, DelegatorNodeStats>,
        ),
      );
    } catch {
      setDelegatedNodes([]);
      setDelegatedNodesTotal(0);
      setDelegatorStatsByNode({});
    }
  }, [address, contractReady]);

  const fetchSelectedNodeData = useCallback(async () => {
    if (!contractReady || !selectedNodeId) {
      setSelectedNode(null);
      setDelegationData(null);
      return;
    }

    try {
      const nodeRaw = await publicClient.readContract({
        address: SILO_NODE_CONTRACT,
        abi: SILO_NODE_ABI,
        functionName: "getNode",
        args: [selectedNodeId],
      });
      const node = parseNodeData(nodeRaw);
      setSelectedNode(node);
    } catch {
      setSelectedNode(null);
    }

    if (!address) {
      setDelegationData(null);
      return;
    }

    try {
      const delegationRaw = await publicClient.readContract({
        address: SILO_NODE_CONTRACT,
        abi: SILO_NODE_ABI,
        functionName: "getDelegation",
        args: [selectedNodeId, address],
      });
      setDelegationData({
        amount: (delegationRaw as any).amount as bigint,
        rewardDebt: (delegationRaw as any).rewardDebt as bigint,
        pendingReward: (delegationRaw as any).pendingReward as bigint,
      });
    } catch {
      setDelegationData(null);
    }
  }, [address, contractReady, selectedNodeId]);

  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchGlobalStats(), fetchLeaderboard(), fetchSelectedNodeData(), fetchDelegatorInfo()]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchDelegatorInfo, fetchGlobalStats, fetchLeaderboard, fetchSelectedNodeData]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (txSuccess && txHash) {
      setTxHash(undefined);
      refreshAll();
    }
  }, [refreshAll, txHash, txSuccess]);

  useEffect(() => {
    fetchSelectedNodeData();
  }, [fetchSelectedNodeData]);

  useEffect(() => {
    const loadSheetNodeData = async () => {
      if (!delegateSheetOpen || !delegateNodeId || !contractReady || (sheetMode !== "unstake" && sheetMode !== "stake")) {
        setSheetNodeData(null);
        return;
      }

      try {
        const nodeRaw = await publicClient.readContract({
          address: SILO_NODE_CONTRACT,
          abi: SILO_NODE_ABI,
          functionName: "getNode",
          args: [delegateNodeId],
        });
        setSheetNodeData(parseNodeData(nodeRaw));
      } catch {
        setSheetNodeData(null);
      }
    };

    loadSheetNodeData();
  }, [contractReady, delegateNodeId, delegateSheetOpen, sheetMode]);

  useEffect(() => {
    const calculateDelegatePreview = async () => {
      if (!delegateSheetOpen || !delegateNodeId || !address || !delegateAmount || !contractReady) {
        setDelegateGasEstimate(null);
        setDelegateGasPrice(null);
        setDelegateEstimateError(null);
        setAmountValidationError(null);
        setEstimatedWeeklyRewards(0n);
        setEstimatedCurrentRewards(0n);
        return;
      }

      try {
        const value = parseEther(delegateAmount);
        setAmountValidationError(null);

        // Strict input validation before any gas estimate.
        if (sheetMode === "delegate" && value < globalStats.minDelegation) {
          setDelegateGasEstimate(null);
          setDelegateGasPrice(null);
          setDelegateEstimateError(null);
          setEstimatedWeeklyRewards(0n);
          setEstimatedCurrentRewards(0n);
          setAmountValidationError(
            `Amount must be at least ${formatAmount(globalStats.minDelegation)} LRS for delegation.`,
          );
          return;
        }
        if (sheetMode === "unstake" && sheetNodeData) {
          if (value > sheetNodeData.providerStake) {
            setDelegateGasEstimate(null);
            setDelegateGasPrice(null);
            setDelegateEstimateError(null);
            setEstimatedWeeklyRewards(0n);
            setEstimatedCurrentRewards(0n);
            setAmountValidationError(
              `Amount exceeds provider stake (${formatAmount(sheetNodeData.providerStake)} LRS).`,
            );
            return;
          }
          if (!sheetNodeData.exitUnlocked) {
            const maxRemovable =
              sheetNodeData.providerStake > globalStats.minProviderStake
                ? sheetNodeData.providerStake - globalStats.minProviderStake
                : 0n;
            if (value > maxRemovable) {
              setDelegateGasEstimate(null);
              setDelegateGasPrice(null);
              setDelegateEstimateError(null);
              setEstimatedWeeklyRewards(0n);
              setEstimatedCurrentRewards(0n);
              setAmountValidationError(
                `Max removable is ${formatAmount(maxRemovable)} LRS while minimum provider stake lock is active.`,
              );
              return;
            }
          }
        }

        // 1) Estimate gas independently.
        try {
          const gasPrice = await publicClient.getGasPrice();
          let gasEstimate: bigint;
          if (sheetMode === "undelegate") {
            gasEstimate = await publicClient.estimateContractGas({
              account: address,
              address: SILO_NODE_CONTRACT,
              abi: SILO_NODE_ABI,
              functionName: "undelegate",
              args: [delegateNodeId, value],
            });
          } else if (sheetMode === "unstake") {
            gasEstimate = await publicClient.estimateContractGas({
              account: address,
              address: SILO_NODE_CONTRACT,
              abi: SILO_NODE_ABI,
              functionName: "removeProviderStake",
              args: [delegateNodeId, value],
            });
          } else if (sheetMode === "stake") {
            gasEstimate = await publicClient.estimateContractGas({
              account: address,
              address: SILO_NODE_CONTRACT,
              abi: SILO_NODE_ABI,
              functionName: "addProviderStake",
              args: [delegateNodeId],
              value,
            });
          } else {
            gasEstimate = await publicClient.estimateContractGas({
              account: address,
              address: SILO_NODE_CONTRACT,
              abi: SILO_NODE_ABI,
              functionName: "delegate",
              args: [delegateNodeId],
              value,
            });
          }
          setDelegateGasEstimate(gasEstimate);
          setDelegateGasPrice(gasPrice);
          setDelegateEstimateError(null);
        } catch {
          setDelegateGasEstimate(null);
          setDelegateGasPrice(null);
          setDelegateEstimateError("Unable to estimate gas for current input (check balance and node rules).");
        }

        // 2) Reward stats are best-effort and should not block gas UI.
        if (sheetMode === "delegate") {
          try {
            const [rewardStatsRaw, nodeRaw, currentEstimateRaw] = await Promise.all([
              publicClient.readContract({
                address: SILO_NODE_CONTRACT,
                abi: SILO_NODE_ABI,
                functionName: "getNodeRewardStats",
                args: [delegateNodeId],
              }),
              publicClient.readContract({
                address: SILO_NODE_CONTRACT,
                abi: SILO_NODE_ABI,
                functionName: "getNode",
                args: [delegateNodeId],
              }),
              publicClient.readContract({
                address: SILO_NODE_CONTRACT,
                abi: SILO_NODE_ABI,
                functionName: "estimateDelegatorEarningLRS",
                args: [delegateNodeId, address],
              }),
            ]);

            setEstimatedCurrentRewards(currentEstimateRaw as bigint);

            const stats = rewardStatsRaw as NodeRewardStats;
            const node = nodeRaw as any;
            const avgDelegatorReward =
              stats.rewardSubmissionCount > 0n ? stats.totalDelegatorRewards / stats.rewardSubmissionCount : 0n;
            const totalDelegated = node.totalDelegated as bigint;
            const projected =
              avgDelegatorReward > 0n && totalDelegated > 0n ? (avgDelegatorReward * value) / totalDelegated : 0n;
            setEstimatedWeeklyRewards(projected);
          } catch {
            setEstimatedWeeklyRewards(0n);
            setEstimatedCurrentRewards(0n);
          }
        } else {
          setEstimatedWeeklyRewards(0n);
          setEstimatedCurrentRewards(0n);
        }
      } catch {
        setDelegateGasEstimate(null);
        setDelegateGasPrice(null);
        setDelegateEstimateError(null);
        setAmountValidationError("Invalid amount format.");
        setEstimatedWeeklyRewards(0n);
        setEstimatedCurrentRewards(0n);
      }
    };

    calculateDelegatePreview();
  }, [address, contractReady, delegateAmount, delegateNodeId, delegateSheetOpen, globalStats.minDelegation, globalStats.minProviderStake, sheetMode, sheetNodeData]);

  const leaderboardRows = useMemo(() => {
    const sorted = [...leaderboardNodes].sort((a, b) => {
      const aTotal = parseBigInt(a.providerStake) + parseBigInt(a.totalDelegated);
      const bTotal = parseBigInt(b.providerStake) + parseBigInt(b.totalDelegated);
      return Number(bTotal - aTotal);
    });
    return sorted.map((entry, index) => ({
      rank: index + 1,
      ...entry,
      providerStakeBigInt: parseBigInt(entry.providerStake),
      totalDelegatedBigInt: parseBigInt(entry.totalDelegated),
      pendingProviderRewardBigInt: parseBigInt(entry.pendingProviderReward),
    }));
  }, [leaderboardNodes]);

  const runAction = async (fn: () => Promise<{ hash: `0x${string}` }>, clear?: () => void) => {
    try {
      const { hash } = await fn();
      setTxHash(hash);
      if (clear) clear();
    } catch (error) {
      alert(error instanceof Error ? error.message : "Transaction failed");
    }
  };

  const openActionSheet = (nodeId: string, mode: "delegate" | "stake" | "unstake" | "undelegate") => {
    setSheetMode(mode);
    setDelegateNodeId(nodeId);
    setDelegateAmount("");
    setDelegateEstimateError(null);
    setAmountValidationError(null);
    setDelegateSheetOpen(true);
  };

  const delegatorSummary = useMemo(() => {
    const totalDelegated = delegatedNodes.reduce((sum, item) => sum + item.delegatedAmount, 0n);
    const totalPending = delegatedNodes.reduce((sum, item) => sum + item.pendingReward, 0n);
    const totalEstimated = delegatedNodes.reduce((sum, item) => sum + item.estimatedNextEpochEarningLRS, 0n);
    return { totalDelegated, totalPending, totalEstimated };
  }, [delegatedNodes]);

  const leaderboardSummary = useMemo(() => {
    const totals = leaderboardRows.reduce(
      (acc, row) => {
        const lastSeen = toRelativeTime(row.stats?.lastContactSuccessAt);
        const isOnline = lastSeen === "Just now" || lastSeen.endsWith("m ago");
        acc.totalStake += row.providerStakeBigInt + row.totalDelegatedBigInt;
        acc.totalStorageBytes += parseBigInt(row.stats?.totalStorage ?? "0");
        acc.totalDelegatorShareBps += row.delegatorShareBps;
        if (isOnline) acc.onlineNodes += 1;
        return acc;
      },
      {
        totalStake: 0n,
        totalStorageBytes: 0n,
        totalDelegatorShareBps: 0,
        onlineNodes: 0,
      },
    );

    const avgDelegatorShare =
      leaderboardRows.length > 0 ? (totals.totalDelegatorShareBps / leaderboardRows.length / 100).toFixed(2) : "0.00";
    const storageGb = Number(totals.totalStorageBytes) / (1024 * 1024 * 1024);

    return {
      totalStake: totals.totalStake,
      onlineNodes: totals.onlineNodes,
      avgDelegatorShare,
      totalStorageGb: Number.isFinite(storageGb) ? storageGb.toFixed(2) : "0.00",
    };
  }, [leaderboardRows]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator orientation="vertical" className="me-2 data-[orientation=vertical]:h-4" />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/silo">SILO</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Nodes</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                  <h1 className="text-3xl font-bold">SILO Nodes</h1>
                </div>
                <div className="mb-6 inline-flex items-center gap-2 rounded-md border border-[#FF8A3C]/40 bg-[#FF8A3C]/10 px-3 py-2 text-xs text-[#FFD3B3]">
                  <RiDatabase2Line className="size-4 text-[#FF8A3C]" />
                  Data updates every 15 minutes.
                </div>
              </div>

              <p className="text-muted-foreground mb-6 max-w-3xl">
                Monitor node leaderboard, delegate from node cards, and review all your delegator stats in one place.
              </p>

              {!contractReady && (
                <Card className="bg-sidebar border border-border mb-6">
                  <CardContent className="p-4 text-sm text-amber-300">
                    Set <code>VITE_SILO_NODE_CONTRACT_ADDRESS</code> to enable on-chain actions in this section.
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Node Stake Locked</div>
                    <div className="text-xl font-semibold">{formatAmount(globalStats.totalProviderPrincipal)} LRS</div>
                  </CardContent>
                </Card>
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Delegator Locked</div>
                    <div className="text-xl font-semibold">{formatAmount(globalStats.totalDelegatorPrincipal)} LRS</div>
                  </CardContent>
                </Card>
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Total Contract Balance</div>
                    <div className="text-xl font-semibold">{formatAmount(globalStats.totalLiabilities)} LRS</div>
                  </CardContent>
                </Card>
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Undistributed Rewards</div>
                    <div className="text-xl">
                      <LrsRewardWithUsd
                        wei={globalStats.freshUndistributedRewards}
                        lrsUsdPrice={lrsPriceUsd}
                        primaryClassName="font-semibold text-[#FF8A3C]"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Tabs defaultValue="leaderboard">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                  <TabsList>
                    <TabsTrigger value="leaderboard">Nodes Leaderboard</TabsTrigger>
                    <TabsTrigger value="delegator">Delegator Info</TabsTrigger>
                  </TabsList>
                  <Button
                    asChild
                    variant="outline"
                    className="border-[#FF8A3C] text-[#FF8A3C] hover:bg-[#FF8A3C]/10 cursor-pointer w-full sm:w-auto shrink-0"
                  >
                    <Link to="/silo/reward-calculator">Reward Calculator</Link>
                  </Button>
                </div>

                <TabsContent value="leaderboard">
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground mb-1">Active Nodes</div>
                        <div className="text-2xl font-semibold">
                          {leaderboardSummary.onlineNodes}
                          <span className="text-sm text-muted-foreground"> / {leaderboardRows.length}</span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground mb-1">Total Staked</div>
                        <div className="text-2xl font-semibold text-[#FF8A3C]">
                          {formatAmount(leaderboardSummary.totalStake)} LRS
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground mb-1">Avg Delegator Share</div>
                        <div className="text-2xl font-semibold">{leaderboardSummary.avgDelegatorShare}%</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground mb-1">Total Storage</div>
                        <div className="text-2xl font-semibold">{leaderboardSummary.totalStorageGb} GB</div>
                      </CardContent>
                    </Card>
                  </div>

                  {leaderboardRows.length === 0 ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="py-10 text-center text-muted-foreground">
                        No node records.
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {leaderboardRows.map((row) => {
                        const totalStake = row.providerStakeBigInt + row.totalDelegatedBigInt;
                        const isSelected = selectedNodeId === row.nodeId;
                        const lastSeen = toRelativeTime(row.stats?.lastContactSuccessAt);
                        const isOnline = lastSeen === "Just now" || lastSeen.endsWith("m ago");
                        const isOwnedByConnectedWallet =
                          !!address && row.provider.toLowerCase() === address.toLowerCase();
                        return (
                          <Card
                            key={row.nodeId}
                            className={`border transition-all duration-200 hover:shadow-lg border-border bg-sidebar hover:border-[#FF8A3C]/60 cursor-pointer`}
                            onClick={() => setSelectedNodeId(row.nodeId)}
                          >
                            <CardContent className="p-4 md:p-5">
                              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-flex items-center rounded-md bg-[#FF8A3C]/15 text-[#FF8A3C] px-2 py-1 text-xs font-semibold">
                                      Rank #{row.rank}
                                    </span>
                                    <span
                                      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                                        isOnline
                                          ? "bg-green-500/15 text-green-400"
                                          : "bg-amber-500/15 text-amber-300"
                                      }`}
                                    >
                                      {isOnline ? "Online" : "Stale"}
                                    </span>
                                  </div>
                                  <div className="font-semibold text-sm md:text-base break-all">Node ID: {row.nodeId}</div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    Provider: {formatAddress(row.provider)} • Last seen {lastSeen}
                                  </div>
                                </div>
                                <div className="text-left lg:text-right">
                                  <div className="text-xs text-muted-foreground">Total Stake</div>
                                  <div className="text-2xl font-bold text-[#FF8A3C]">{formatAmount(totalStake)} LRS</div>
                                  <div className="mt-2 flex flex-wrap gap-2 lg:justify-end">
                                    <Button
                                      size="sm"
                                      className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90 cursor-pointer"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openActionSheet(row.nodeId, isOwnedByConnectedWallet ? "stake" : "delegate");
                                      }}
                                    >
                                      {isOwnedByConnectedWallet ? "Stake More" : "Delegate"}
                                    </Button>
                                    {isOwnedByConnectedWallet && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="cursor-pointer"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            openActionSheet(row.nodeId, "unstake");
                                          }}
                                        >
                                          Unstake
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="cursor-pointer"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            runAction(() => actions.claimProviderReward(row.nodeId));
                                          }}
                                        >
                                          Claim Provider Reward
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
                                <div className="rounded-md border border-border bg-background/30 p-3">
                                  <div className="text-xs text-muted-foreground mb-1">Provider Stake</div>
                                  <div className="font-semibold">{formatAmount(row.providerStakeBigInt)} LRS</div>
                                </div>
                                <div className="rounded-md border border-border bg-background/30 p-3">
                                  <div className="text-xs text-muted-foreground mb-1">Total Delegated</div>
                                  <div className="font-semibold">{formatAmount(row.totalDelegatedBigInt)} LRS</div>
                                </div>
                                <div className="rounded-md border border-border bg-background/30 p-3">
                                  <div className="text-xs text-muted-foreground mb-1">Delegator Reward Share</div>
                                  <div className="font-semibold">{(row.delegatorShareBps / 100).toFixed(2)}%</div>
                                </div>
                                <div className="rounded-md border border-border bg-background/30 p-3">
                                  <div className="text-xs text-muted-foreground mb-1">Pending Provider Reward</div>
                                  <LrsRewardWithUsd wei={row.pendingProviderRewardBigInt} lrsUsdPrice={lrsPriceUsd} />
                                </div>
                                <div className="rounded-md border border-border bg-background/30 p-3">
                                  <div className="text-xs text-muted-foreground mb-1">Storage</div>
                                  <div className="font-semibold">{formatStorageGb(row.stats?.totalStorage)} GB</div>
                                </div>
                                <div className="rounded-md border border-border bg-background/30 p-3">
                                  <div className="text-xs text-muted-foreground mb-1">Estimated Reward</div>
                                  <LrsRewardWithUsd
                                    wei={parseBigInt(row.estimatedReward?.wei || "0")}
                                    lrsUsdPrice={lrsPriceUsd}
                                  />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="delegator">
                  {!address ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-6 text-center text-muted-foreground">
                        Connect wallet to view your delegated nodes and stats.
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      <Card className="bg-sidebar border border-border mb-4">
                        <CardContent className="p-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="text-sm font-medium">Delegator Portfolio Overview</div>
                            <div className="text-xs text-muted-foreground">
                              Track delegated positions, pending rewards, and next-epoch projections across all nodes.
                            </div>
                          </div>
                          <Badge variant="outline" className="w-fit border-[#FF8A3C] text-[#FF8A3C]">
                            {delegatedNodes.length} Active Delegations
                          </Badge>
                        </CardContent>
                      </Card>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <Card className="bg-sidebar border border-border">
                          <CardContent className="p-4">
                            <div className="text-sm text-muted-foreground">Delegated Nodes</div>
                            <div className="text-2xl font-bold">{delegatedNodesTotal}</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-sidebar border border-border">
                          <CardContent className="p-4">
                            <div className="text-sm text-muted-foreground">Total Delegated</div>
                            <div className="text-2xl font-bold text-[#FF8A3C]">
                              {formatAmount(delegatorSummary.totalDelegated)} LRS
                            </div>
                          </CardContent>
                        </Card>
                        <Card className="bg-sidebar border border-border">
                          <CardContent className="p-4">
                            <div className="text-sm text-muted-foreground">Pending Rewards</div>
                            <div className="text-2xl">
                              <LrsRewardWithUsd
                                wei={delegatorSummary.totalPending}
                                lrsUsdPrice={lrsPriceUsd}
                                primaryClassName="font-bold text-2xl"
                              />
                            </div>
                          </CardContent>
                        </Card>
                        <Card className="bg-sidebar border border-border">
                          <CardContent className="p-4">
                            <div className="text-sm text-muted-foreground">Est. Next Reward Distribution</div>
                            <div className="text-2xl">
                              <LrsRewardWithUsd
                                wei={delegatorSummary.totalEstimated}
                                lrsUsdPrice={lrsPriceUsd}
                                primaryClassName="font-bold text-2xl"
                              />
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      <div className="space-y-3">
                        {delegatedNodes.length === 0 ? (
                          <Card className="bg-sidebar border border-border">
                            <CardContent className="p-6 text-center text-muted-foreground">
                              No delegated nodes found.
                            </CardContent>
                          </Card>
                        ) : (
                          delegatedNodes.map((item) => {
                            const stats = delegatorStatsByNode[item.nodeId];
                            const estNextUsd = formatUsdFromLrs(
                              Number(formatEther(item.estimatedNextEpochEarningLRS)),
                              lrsPriceUsd,
                            );
                            return (
                              <Card key={item.nodeId} className="bg-sidebar border border-border hover:border-[#FF8A3C]/40 transition-colors">
                                <CardContent className="p-4">
                                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-3">
                                    <div className="min-w-0">
                                      <div className="font-semibold break-all">{item.nodeId}</div>
                                      <div className="text-xs text-muted-foreground mt-1">
                                        Provider {formatAddress(item.provider)} • Share {(item.delegatorShareBps / 100).toFixed(2)}%
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Badge
                                        variant="outline"
                                        className="h-auto border-[#FF8A3C] text-[#FF8A3C] py-1.5 px-2.5 gap-1 flex flex-col items-start"
                                      >
                                        <span className="text-xs font-semibold leading-tight">
                                          Est. Next {formatAmount(item.estimatedNextEpochEarningLRS)} LRS
                                        </span>
                                        {estNextUsd && (
                                          <span className="text-[10px] font-normal text-muted-foreground tabular-nums leading-tight">
                                            ≈ {estNextUsd}
                                          </span>
                                        )}
                                      </Badge>
                                      <Button
                                        size="sm"
                                        className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90 cursor-pointer"
                                        onClick={() => openActionSheet(item.nodeId, "delegate")}
                                      >
                                        Delegate More
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="cursor-pointer"
                                        onClick={() => openActionSheet(item.nodeId, "undelegate")}
                                      >
                                        Undelegate
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="cursor-pointer"
                                        onClick={() => runAction(() => actions.claimDelegatorReward(item.nodeId))}
                                      >
                                        Claim Reward
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
                                    <div className="rounded-md border border-border bg-background/30 p-3">
                                      <div className="text-xs text-muted-foreground mb-1">Delegated Amount</div>
                                      <div className="font-semibold">{formatAmount(item.delegatedAmount)} LRS</div>
                                    </div>
                                    <div className="rounded-md border border-border bg-background/30 p-3">
                                      <div className="text-xs text-muted-foreground mb-1">Pending Reward</div>
                                      <LrsRewardWithUsd wei={item.pendingReward} lrsUsdPrice={lrsPriceUsd} />
                                    </div>
                                    <div className="rounded-md border border-border bg-background/30 p-3">
                                      <div className="text-xs text-muted-foreground mb-1">Total Node Delegation</div>
                                      <div className="font-semibold">{formatAmount(item.totalNodeDelegation)} LRS</div>
                                    </div>
                                    <div className="rounded-md border border-border bg-background/30 p-3">
                                      <div className="text-xs text-muted-foreground mb-1">Total Delegated (You)</div>
                                      <div className="font-semibold">{formatAmount(stats?.totalDelegated ?? 0n)} LRS</div>
                                    </div>
                                    <div className="rounded-md border border-border bg-background/30 p-3">
                                      <div className="text-xs text-muted-foreground mb-1">Claimed Rewards</div>
                                      <LrsRewardWithUsd
                                        wei={stats?.totalClaimedRewards ?? 0n}
                                        lrsUsdPrice={lrsPriceUsd}
                                      />
                                    </div>
                                    <div className="rounded-md border border-border bg-background/30 p-3">
                                      <div className="text-xs text-muted-foreground mb-1">Last Action</div>
                                      <div className="font-semibold text-xs">{formatDateTime(stats?.lastActionAt ?? 0n)}</div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </SidebarInset>

      <Sheet open={delegateSheetOpen} onOpenChange={setDelegateSheetOpen}>
        <SheetContent side="right" className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="text-[#FF8A3C]">
              {sheetMode === "stake"
                ? "Stake More (Provider)"
                : sheetMode === "unstake"
                  ? "Unstake (Provider)"
                : sheetMode === "undelegate"
                  ? "Undelegate from Node"
                  : "Delegate to Node"}
            </SheetTitle>
            <SheetDescription>
              Enter amount, review gas fee and{" "}
              {sheetMode === "stake"
                ? "stake"
                : sheetMode === "unstake"
                  ? "unstake"
                  : sheetMode === "undelegate"
                    ? "withdrawal"
                    : "reward"}{" "}
              estimates, then submit.
            </SheetDescription>
          </SheetHeader>
          <div className="p-4 space-y-4">
            <div className="rounded-md border border-border bg-background/30 p-3">
              <div className="text-xs text-muted-foreground mb-1">Node</div>
              <div className="text-sm font-semibold break-all">{delegateNodeId || "-"}</div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Amount (LRS)</label>
              <Input
                value={delegateAmount}
                onChange={(event) => setDelegateAmount(event.target.value)}
                placeholder="e.g. 1.25"
              />
              {sheetMode === "delegate" && (
                <div className="text-xs text-muted-foreground mt-2">
                  Min delegation: <span className="text-foreground font-semibold">{formatAmount(globalStats.minDelegation)} LRS</span>
                </div>
              )}
              {sheetMode === "unstake" && (
                <div className="mt-2 rounded-md border border-border bg-background/30 p-3 space-y-1 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Current Provider Stake</span>
                    <span className="font-semibold">{formatAmount(sheetNodeData?.providerStake ?? 0n)} LRS</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Minimum Stake Lock</span>
                    <span className="font-semibold">{formatAmount(globalStats.minProviderStake)} LRS</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Graceful Exit</span>
                    <span className={sheetNodeData?.exitUnlocked ? "text-green-400 font-semibold" : "text-amber-300 font-semibold"}>
                      {sheetNodeData?.exitUnlocked
                        ? "Unlocked (min lock lifted)"
                        : sheetNodeData?.exitRequested
                          ? "Requested (still locked)"
                          : "Not requested (locked)"}
                    </span>
                  </div>
                  {!sheetNodeData?.exitUnlocked && (
                    <div className="text-amber-300">
                      Unstake cannot reduce provider stake below minimum until graceful exit is unlocked.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-md border border-border bg-background/30 p-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Estimated Gas Fee</span>
                <span className="font-semibold">
                  {delegateGasEstimate && delegateGasPrice ? (
                    `${formatAmount(delegateGasEstimate * delegateGasPrice)} LRS`
                  ) : (
                    <span className="text-muted-foreground">{delegateEstimateError ? "Unavailable" : "Calculating..."}</span>
                  )}
                </span>
              </div>
              {sheetMode === "delegate" ? (
                <>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Estimated Weekly Rewards</span>
                    <div className="text-right">
                      <LrsRewardWithUsd
                        wei={estimatedWeeklyRewards}
                        lrsUsdPrice={lrsPriceUsd}
                        primaryClassName="font-semibold text-[#FF8A3C]"
                      />
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Current Est. Weekly (Wallet)</span>
                    <div className="text-right">
                      <LrsRewardWithUsd wei={estimatedCurrentRewards} lrsUsdPrice={lrsPriceUsd} />
                    </div>
                  </div>
                </>
              ) : sheetMode === "stake" ? (
                <div className="text-xs text-muted-foreground">
                  Stake-more action increases provider principal and can improve provider-side rewards.
                </div>
              ) : sheetMode === "unstake" ? (
                <div className="text-xs text-muted-foreground">
                  Unstake removes provider principal from this node based on entered amount.
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Undelegate returns principal back to your wallet based on entered amount.
                </div>
              )}
              {delegateEstimateError && <div className="text-xs text-amber-300">{delegateEstimateError}</div>}
              {amountValidationError && <div className="text-xs text-red-400">{amountValidationError}</div>}
            </div>

            <div className="flex gap-3">
              <Button
                className="flex-1 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                disabled={!delegateNodeId || !delegateAmount || !!amountValidationError}
                onClick={() =>
                  runAction(
                    () =>
                      sheetMode === "stake"
                        ? actions.addProviderStake(delegateNodeId, delegateAmount)
                        : sheetMode === "unstake"
                          ? actions.removeProviderStake(delegateNodeId, delegateAmount)
                        : sheetMode === "undelegate"
                          ? actions.undelegateFromNode(delegateNodeId, delegateAmount)
                          : actions.delegateToNode(delegateNodeId, delegateAmount),
                    () => {
                      setDelegateAmount("");
                      setDelegateSheetOpen(false);
                    },
                  )
                }
              >
                {sheetMode === "stake"
                  ? "Submit Stake"
                  : sheetMode === "unstake"
                    ? "Submit Unstake"
                    : sheetMode === "undelegate"
                      ? "Submit Undelegate"
                      : "Submit Delegate"}
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setDelegateSheetOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </SidebarProvider>
  );
}
